import { GQL_ID } from '@@partner:types:graphql'
import { CourierWorkShift } from '@@partner:types:app'

import React from 'react'
import { generatePath, Link, useHistory, useParams } from 'react-router-dom'

import { useFetchAllCourierWorkShifts } from '@@partner:graphql/query/courier/allCourierWorkShifts.gql'

import { Col, Grid, Panel, Row, Avatar, Tag, Timeline, Modal, Button } from 'rsuite'

import AppLoader from '@components/common/AppLoader'

import { convertToInt, formatDate } from '@@partner:helpers/util'

import { ROUTE_LOGISTIC_SINGLE_COURIER } from '@@partner:config/routes'

import './VehicleShiftsList.scss'
import { formatWorkshiftStatusLabel } from '@@partner:helpers/formatters/workshift'

type VehicleShiftsListProps = {
  vehicleId: GQL_ID
}

export default function VehicleShiftsList ({
  vehicleId
}: VehicleShiftsListProps) {
  const { courierWorkshifts, loading, error, fetchNextPage, hasMore } = useFetchAllCourierWorkShifts({
    filter: {
      fields: [ 'vehicle_id' ],
      op: 'eq',
      value: vehicleId
    },
    pager: {
      limit: 10
    }
  })

  if (loading) {
    return <AppLoader.RowsWithText 
      rows={3}
      columns={1}
      rowHeight={24}
      rowMargin={12}
      content="Загружаем смены"
    />
  }
  if (error) {
    return (
      <Panel bordered header="Ошибка загрузки" className="text-center">
        <p className="text-danger">Не удалось загрузить список смен</p>
      </Panel>
    )
  }

  if (courierWorkshifts.length === 0) {
    return (
      <Panel bordered header="Нет смен" className="text-center">
        <p className="text-muted">Для данного транспорта еще не назначены смены</p>
      </Panel>
    )
  }

  return (
    <div className='VehicleShiftsList'>
      { courierWorkshifts.map(cwf => (
        <VehicleShiftElement 
          key={cwf.id}
          shift={cwf}
          className='VehicleShiftsList__Element'
        />
      ))}
      { hasMore && (
        <div>
          <Button onClick={fetchNextPage} loading={loading} appearance='primary'>
            Загрузить еще
          </Button>
        </div>
      )}
    </div>
  )
}


export function VehicleShiftElement ({
  shift,
  className
}: {
  shift: CourierWorkShift
  className?: string
}) {
  const { brandSlug } = useParams<any>()
  const history = useHistory()
  const [ selectedImage, setSelectedImage ] = React.useState<string | null>(null)

  const handleNavToCourier = (courierId: GQL_ID) => {
    history.push(generatePath(ROUTE_LOGISTIC_SINGLE_COURIER, {
      brandSlug,
      courierId
    }))
  }
  
  // Храним текущий индекс изображения и список URL для навигации по галерее
  const [ currentImageIndex, setCurrentImageIndex ] = React.useState<number>(0)
  
  const openImageModal = (url: string) => {
    // Находим индекс текущего изображения
    const fileIndex = shift.files?.findIndex(file => file.url === url) || 0
    setCurrentImageIndex(fileIndex)
    setSelectedImage(url)
  }
  
  const closeImageModal = () => {
    setSelectedImage(null)
  }
  
  const handlePrevImage = () => {
    if (!shift.files || !shift.files.length) return
    
    const newIndex = (currentImageIndex - 1 + shift.files.length) % shift.files.length
    setCurrentImageIndex(newIndex)
    if (shift.files[newIndex].url) {
      setSelectedImage(shift.files[newIndex].url)
    }
  }
  
  const handleNextImage = () => {
    if (!shift.files || !shift.files.length) return
    
    const newIndex = (currentImageIndex + 1) % shift.files.length
    setCurrentImageIndex(newIndex)
    if (shift.files[newIndex].url) {
      setSelectedImage(shift.files[newIndex].url)
    }
  }

  const statusInfo = formatWorkshiftStatusLabel(shift.status)
  const startDate = new Date(convertToInt(shift.plannedStartAt))
  const endDate = new Date(convertToInt(shift.plannedFinishAt))
  
  // Определяем класс статуса для стилизации
  const statusClass = statusInfo?.label.toLowerCase().replace(/\s+/g, '-') || ''

  return (
    <div className={className}>
      <Panel
        bordered
        header={
          <div className="VehicleShiftsList__ShiftHeader">
            <div className="VehicleShiftsList__ShiftDate">
              Смена {formatDate(startDate)}
            </div>
            <Tag color={statusInfo?.color as any} className="VehicleShiftsList__ShiftStatus">
              {statusInfo?.label}
            </Tag>
          </div>
        }
        className={`status-${statusClass}`}
      >
        <Grid fluid>
          <Row className="mb-3">
            <Col xs={24}>
              <Timeline className="VehicleShiftsList__Timeline">
                <Timeline.Item 
                  dot={<div className="VehicleShiftsList__TimelineDot VehicleShiftsList__TimelineDot--planned"></div>}
                >
                  <p><strong>Запланировано</strong>: с {formatDate(startDate)} по {formatDate(endDate)}</p>
                </Timeline.Item>

                {shift.startedAt && (
                  <Timeline.Item 
                    dot={<div className="VehicleShiftsList__TimelineDot VehicleShiftsList__TimelineDot--started"></div>}
                  >
                    <p><strong>Начата</strong>: {formatDate(new Date(convertToInt(shift.startedAt)))}</p>
                  </Timeline.Item>
                )}

                {shift.finishedAt && (
                  <Timeline.Item 
                    dot={<div className="VehicleShiftsList__TimelineDot VehicleShiftsList__TimelineDot--finished"></div>}
                  >
                    <p><strong>Закончена</strong>: {formatDate(new Date(convertToInt(shift.finishedAt)))}</p>
                  </Timeline.Item>
                )}
              </Timeline>
            </Col>
          </Row>

          {shift.courier?.id && (
            <Row>
              <Col xs={24}>
                <div 
                  className="VehicleShiftsList__CourierCard"
                  onClick={() => shift.courier?.id && handleNavToCourier(shift.courier.id)}
                >
                  <Avatar circle size="md" style={{ backgroundColor: '#3498ff' }}>
                    {shift.courier.fullName?.charAt(0).toUpperCase() || 'К'}
                  </Avatar>
                  <div className="VehicleShiftsList__CourierInfo">
                    <div className="VehicleShiftsList__CourierName">
                      {shift.courier.fullName}
                    </div>
                    <a 
                      href={`tel:+${shift.courier.phone}`}
                      className="VehicleShiftsList__CourierPhone"
                      onClick={(e) => e.stopPropagation()}
                    >
                      +{shift.courier.phone}
                    </a>
                  </div>
                </div>
              </Col>
            </Row>
          )}
          { shift.files && shift.files.length > 0 && (
            <Row className="mt-3">
              <Col xs={24}>
                <div className="VehicleShiftsList__ImagesTitle">
                  <strong>Фото транспорта:</strong>
                </div>
                <div className="VehicleShiftsList__ImagesGrid">
                  { shift.files.map((file, index) => (
                    <div 
                      key={`image:${index}`} 
                      className="VehicleShiftsList__ImageThumbnail"
                      onClick={() => file.url && openImageModal(file.url)}
                    >
                      <img src={file.url} alt={`Фото ${index + 1}`} />
                      <div className="VehicleShiftsList__ImageOverlay">
                        <span className="VehicleShiftsList__ImageZoom">🔍</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          )}
        </Grid>
      </Panel>
      
      {/* Модальное окно для просмотра изображений */}
      <Modal 
        size="lg" 
        open={!!selectedImage} 
        onClose={closeImageModal} 
        className="VehicleShiftsList__ImageModal"
      >
        <Modal.Header>
          <Modal.Title>
            Просмотр изображения {shift.files && shift.files.length > 0 ? 
              `${currentImageIndex + 1} из ${shift.files.length}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="VehicleShiftsList__FullImage">
            {selectedImage && <img src={selectedImage} alt="Увеличенное изображение" />}
            
            {shift.files && shift.files.length > 1 && (
              <div className="VehicleShiftsList__ImageNavigation">
                <Button 
                  onClick={handlePrevImage} 
                  appearance="ghost"
                  className="VehicleShiftsList__NavButton VehicleShiftsList__NavButton--prev"
                >
                  <span className="VehicleShiftsList__NavButtonDesktop">&laquo; Назад</span>
                  <span className="VehicleShiftsList__NavButtonMobile">&laquo;</span>
                </Button>
                <Button 
                  onClick={handleNextImage} 
                  appearance="ghost"
                  className="VehicleShiftsList__NavButton VehicleShiftsList__NavButton--next"
                >
                  <span className="VehicleShiftsList__NavButtonDesktop">Вперед &raquo;</span>
                  <span className="VehicleShiftsList__NavButtonMobile">&raquo;</span>
                </Button>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            onClick={closeImageModal} 
            appearance="ghost" 
            className="VehicleShiftsList__CloseButton"
          >
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}